import {
  LOCKUP_HORIZONTAL_MARK_TRANSFORM,
  LOCKUP_HORIZONTAL_VIEWBOX,
  LOCKUP_HORIZONTAL_WORDMARK_TRANSFORM,
  LOCKUP_STACKED_MARK_TRANSFORM,
  LOCKUP_STACKED_VIEWBOX,
  LOCKUP_STACKED_WORDMARK_TRANSFORM,
  MARK_PATH,
  WORDMARK_PATH,
} from "./paths";
import {
  INK_COLOR,
  TILE_COLOR,
  type BrandColor,
  type BrandVariant,
} from "./tokens";

export type BrandLayout = "horizontal" | "stacked";

// Horizontal viewBox 3344×1000 → ratio 3.344:1. Size sets height.
// Stacked viewBox 3488×1820 → ratio ≈ 1.917:1. Size sets height.
// Floors from PR #191 pressure tests: horizontal legible at ~32px,
// stacked at ~64px — consumers should honor these minimums.
const LAYOUT = {
  horizontal: {
    viewBox: LOCKUP_HORIZONTAL_VIEWBOX,
    ratio: 3344 / 1000,
    width: 3344,
    height: 1000,
    markTransform: LOCKUP_HORIZONTAL_MARK_TRANSFORM,
    wordmarkTransform: LOCKUP_HORIZONTAL_WORDMARK_TRANSFORM,
  },
  stacked: {
    viewBox: LOCKUP_STACKED_VIEWBOX,
    ratio: 3488 / 1820,
    width: 3488,
    height: 1820,
    markTransform: LOCKUP_STACKED_MARK_TRANSFORM,
    wordmarkTransform: LOCKUP_STACKED_WORDMARK_TRANSFORM,
  },
} as const;

export interface AdvaLogoProps {
  size?: number;
  layout?: BrandLayout;
  color?: BrandColor;
  variant?: BrandVariant;
  className?: string;
  title?: string;
  "aria-label"?: string;
}

export function AdvaLogo({
  size = 24,
  layout = "horizontal",
  color = "dark",
  variant = "transparent",
  className,
  title,
  "aria-label": ariaLabel,
}: AdvaLogoProps) {
  const accessibleName = ariaLabel ?? title ?? "Adva logo";
  const spec = LAYOUT[layout];
  const ink = INK_COLOR[color];
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={spec.viewBox}
      width={Math.round(size * spec.ratio)}
      height={size}
      fillRule="evenodd"
      role="img"
      aria-label={accessibleName}
      className={className}
    >
      {title ? <title>{title}</title> : null}
      {variant === "solid" ? (
        <rect
          width={spec.width}
          height={spec.height}
          fill={TILE_COLOR[color]}
        />
      ) : null}
      <g transform={spec.markTransform} fill={ink}>
        <path d={MARK_PATH} />
      </g>
      <g transform={spec.wordmarkTransform} fill={ink}>
        <path d={WORDMARK_PATH} />
      </g>
    </svg>
  );
}
